import ReactGA from 'react-ga4';
import { Dispatch } from 'redux';
import { UTMTags } from '@mm/hooks';
import { gtag } from '@mm/plugins/GoogleAnalytics';
import AuthenticatedController from '../controllers/AuthenticatedController';
import FacebookSessionController from '../controllers/FacebookSessionController';
import { Persona } from '../controllers/SessionController';
import { facebookSessionCreateHandler } from '../errorHandlers/facebook-session-handler';
import { UserAttributes } from '../model/CompanyUsersModel';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import companyCRUDActions from './CompanyCRUDActions';
import CrudActionContainer from './CrudActionContainer';
import sessionCRUDActions from './SessionCRUDActions';
import { extractAffiliateData } from '@mm/utils';

export type CreatePayload = {
  src?: string;
  notFbUser?: boolean;
  affiliateCode?: string;
  affiliateVisitorId?: string;
  facebookToken: string;
  inviteToken?: string;
  standaloneToken?: string;
  userAttributes?: Partial<UserAttributes> & { signupForm?: Persona };
  email?: string;
  freeTrial?: boolean;
};

class FacebookSessionCRUDActions extends CrudActionContainer {
  constructor() {
    super('FACEBOOK_SESSION');
  }

  create(
    payload: CreatePayload,
    extraOptions?: {
      throwErrors?: boolean;
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));

      try {
        const affiliateData = extractAffiliateData();
        const affiliatePayload = {
          affiliateCode: affiliateData?.code,
          affiliateVisitorId: affiliateData?.visitorId,
        };
        // allow outer scope override inferred affiliate payload
        const response = await FacebookSessionController.post({
          ...affiliatePayload,
          ...payload,
        });
        dispatch(sessionCRUDActions.resetInviteToken());
        if (response.error) {
          // Not exactly sure if this method somehow exists but it
          // looks like a type error
          //
          // @ts-ignore
          dispatch(this.clear());
          return response;
        }
        UTMTags.clear();

        if (response?.newlyCreatedUser && !payload.inviteToken) {
          ReactGA.set({ page: '/new-registrant' });
          gtag('event', 'mobilemonkey-signup');
        }

        AuthenticatedController.storeSession(response);
        dispatch(this.retrieved(response));
        return response;
      } catch (error) {
        if (extraOptions?.throwErrors) {
          throw error;
        }
        facebookSessionCreateHandler(dispatch)({ error, payload });
      }
    };
  }
}

const facebookSessionCRUDActions = new FacebookSessionCRUDActions();

export default facebookSessionCRUDActions;

export const useFacebookSessionCRUDActions = getActionHookBuilder(
  facebookSessionCRUDActions,
  ['create'],
);
